import { Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import theme from '@/theme';
import { useWebsiteFeatures } from '@/hooks/useWebsiteFeatures';
import { PageSection } from '@/components/Layout/PageLayout';
import type { FeatureApp } from '@/services/featureListService';

interface AppColumn {
  app: FeatureApp;
  label: string;
  Icon: typeof DesktopWindowsOutlinedIcon;
}

// Desktop first: this is the desktop app's page, so its column leads and is the
// one highlighted.
const APP_COLUMNS: AppColumn[] = [
  { app: 'desktop_app', label: 'Desktop app', Icon: DesktopWindowsOutlinedIcon },
  { app: 'website', label: 'Web app', Icon: LanguageOutlinedIcon },
  { app: 'plugin', label: 'Chrome extension', Icon: ExtensionOutlinedIcon },
];

const HIGHLIGHT_RADIUS = '12px';

const appCellSx = (app: FeatureApp) => ({
  width: { xs: 68, sm: 128 },
  px: { xs: 0.5, sm: 1.5 },
  ...(app === 'desktop_app' && { bgcolor: theme.palette.neutral.lightBackground }),
});

/**
 * Features an admin flagged "Show on website" (A→Z), compared across the
 * desktop app, web app and Chrome extension. Renders nothing until there is at
 * least one row, so a failed or empty read leaves the page as it was.
 */
const DesktopFeatureComparison = () => {
  const features = useWebsiteFeatures();

  if (features.length === 0) {
    return null;
  }

  const desktopOnlyCount = features.filter(
    ({ availability }) => availability.desktop_app && !availability.website && !availability.plugin,
  ).length;

  return (
    <PageSection title="Compare apps">
      <Stack gap="1.25rem">
        <Typography variant="body2" color={theme.palette.neutral.greyFont}>
          What you can do in the desktop app, the web app, and the Chrome extension.
        </Typography>

        {desktopOnlyCount > 0 && (
          <Stack flexDirection="row" alignItems="center" gap="0.875rem">
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: HIGHLIGHT_RADIUS,
                bgcolor: theme.palette.neutral.lightBackground,
                color: theme.palette.primary.main,
              }}
            >
              <DesktopWindowsOutlinedIcon />
            </Box>
            <Stack gap="0.125rem">
              <Typography variant="body1" fontWeight="700">
                {desktopOnlyCount} {desktopOnlyCount === 1 ? 'feature' : 'features'} only in the desktop app
              </Typography>
              <Typography variant="body2" color={theme.palette.neutral.greyFont}>
                Not available in the web app or the Chrome extension.
              </Typography>
            </Stack>
          </Stack>
        )}

        <Box sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            aria-label="Feature availability by app"
            sx={{
              '& .MuiTableCell-root': { borderColor: theme.palette.neutral.otherBorder },
              // Table-level so it outranks a cell's own sx class (the rounded desktop cell).
              '& tbody tr:last-child > *': { borderBottom: 0 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 0, verticalAlign: 'bottom' }}>
                  <Typography variant="caption" fontWeight="700" color={theme.palette.neutral.greyFont}>
                    Feature
                  </Typography>
                </TableCell>
                {APP_COLUMNS.map(({ app, label, Icon }) => (
                  <TableCell
                    key={app}
                    align="center"
                    sx={{
                      ...appCellSx(app),
                      py: 1.25,
                      verticalAlign: 'bottom',
                      color: app === 'desktop_app' ? theme.palette.primary.main : theme.palette.neutral.greyFont,
                      ...(app === 'desktop_app' && {
                        borderTopLeftRadius: HIGHLIGHT_RADIUS,
                        borderTopRightRadius: HIGHLIGHT_RADIUS,
                      }),
                    }}
                  >
                    <Stack alignItems="center" gap="0.25rem">
                      <Icon sx={{ fontSize: 22 }} />
                      <Typography variant="caption" fontWeight="700" lineHeight={1.2} color="inherit">
                        {label}
                      </Typography>
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {features.map((feature, index) => {
                const isLastRow = index === features.length - 1;

                return (
                  <TableRow key={feature.key}>
                    <TableCell component="th" scope="row" sx={{ pl: 0, pr: 1, py: 1.25 }}>
                      <Stack flexDirection="row" alignItems="center" flexWrap="wrap" gap="0.5rem">
                        <Typography
                          variant="body2"
                          fontWeight="500"
                          sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                        >
                          {feature.label}
                        </Typography>
                        {feature.beta && (
                          <Chip
                            label="Beta"
                            size="small"
                            sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', fontWeight: 500 }}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    {APP_COLUMNS.map(({ app }) => (
                      <TableCell
                        key={app}
                        align="center"
                        sx={{
                          ...appCellSx(app),
                          ...(isLastRow &&
                            app === 'desktop_app' && {
                              borderBottomLeftRadius: HIGHLIGHT_RADIUS,
                              borderBottomRightRadius: HIGHLIGHT_RADIUS,
                            }),
                        }}
                      >
                        {feature.availability[app] ? (
                          <CheckCircleOutlineIcon
                            titleAccess="Available"
                            sx={{ fontSize: 20, verticalAlign: 'middle', color: theme.palette.success.main }}
                          />
                        ) : (
                          // greyFont, not the lighter dashBorder: this ✗ is the only thing saying
                          // "not available", so it needs 3:1 contrast on the tinted column too.
                          <CloseIcon
                            titleAccess="Not available"
                            sx={{ fontSize: 20, verticalAlign: 'middle', color: theme.palette.neutral.greyFont }}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </PageSection>
  );
};

export default DesktopFeatureComparison;
